import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GlobalHubsMap, HUBS } from "@/components/GlobalHubsMap";
import { StarRating, Loading, EmptyState } from "@/components/shared";
import { Building2, Flag, ArrowRight, MapPin } from "lucide-react";

export default function Portfolio() {
  const [companies, setCompanies] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Portfolio: iGaming employers and hubs | iGamingTruth";
    api.get("/companies", { params: { sort: "reviews" } })
      .then(({ data }) => setCompanies(data))
      .catch(() => setCompanies([]));
    window.scrollTo(0, 0);
    return () => { document.title = "iGamingTruth"; };
  }, []);

  return (
    <main>
      {/* Global hubs hero */}
      <section className="border-b border-[#DDDAD1]">
        <div className="mx-auto max-w-[1800px] px-6 py-10 sm:px-10 sm:py-16 lg:px-16">
          <p className="eyebrow mb-4">Portfolio</p>
          <h1 className="max-w-5xl text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
            The industry runs from a handful of <span className="text-teal-400">hubs.</span>
          </h1>
          <p className="mt-6 max-w-4xl text-base leading-relaxed text-slate-300">
            Online gambling is a small, mobile industry clustered around a short list of licensing
            jurisdictions and operations centres. These are the markets our verified reviews cover,
            from Malta and Gibraltar to Manila and San José.
          </p>

          <div className="mt-8 overflow-hidden rounded-[10px] border border-[#DDDAD1] bg-[#FCFBF8] sm:mt-10" data-testid="portfolio-map">
            <div className="aspect-[2/1] w-full">
              <GlobalHubsMap onHubClick={(name) => nav(`/companies?market=${encodeURIComponent(name)}`)} />
            </div>
          </div>

          {/* Hub chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {HUBS.map((h) => (
              <Link
                key={h.name}
                to={`/companies?market=${encodeURIComponent(h.name)}`}
                data-testid="hub-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#DDDAD1] bg-white px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-300 transition-colors hover:border-teal-400/50 hover:text-teal-600"
              >
                <MapPin className="h-3 w-3 text-teal-400" aria-hidden="true" /> {h.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Company showcase */}
      <section className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 sm:py-16 lg:px-16">
        <p className="eyebrow mb-3">On the platform</p>
        <h2 className="text-3xl font-bold text-ink">Employers our members have reviewed</h2>
        <p className="mt-3 max-w-4xl text-slate-400">
          Operators, platform providers, game studios, payment firms and affiliates, rated by the
          people on their payroll.
        </p>

        {companies === null ? (
          <Loading label="Loading employers" />
        ) : companies.length === 0 ? (
          <div className="mt-10">
            <EmptyState icon={Building2} title="No employers yet" subtitle="Check back soon as profiles are added." />
          </div>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {companies.map((c, i) => (
              <Link
                key={c.id}
                to={`/company/${c.id}`}
                data-testid="portfolio-company"
                className="card-surface lift flex flex-col p-6 animate-fade-up"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-ink">{c.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{c.location || "-"}</p>
                  </div>
                  <span className="font-heading text-3xl font-bold text-teal-400">{c.avg_overall || "-"}</span>
                </div>
                <p className="mt-3 line-clamp-2 text-sm text-slate-400">{c.description}</p>
                <div className="mt-5 flex items-center justify-between border-t border-[#DDDAD1] pt-4">
                  <StarRating value={c.avg_overall} />
                  <span className="flex items-center gap-3 text-xs text-slate-400">
                    {c.red_flag_count > 0 && (
                      <span className="flex items-center gap-1 text-coral-500"><Flag className="h-3.5 w-3.5" /> {c.red_flag_count}</span>
                    )}
                    {c.review_count} reviews
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/companies" data-testid="portfolio-browse" className="btn-teal text-sm">
            Browse all employers <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link to="/p/about" className="inline-flex items-center gap-2 rounded-[8px] border border-[#DDDAD1] px-6 py-3 text-sm text-slate-200 transition-colors hover:border-teal-400/50">
            Why this site exists
          </Link>
        </div>
      </section>
    </main>
  );
}
